"use client";

import { ExternalLink } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { claimPlayback } from "./single-playback";

const RETRY_DELAY_MS = 1000;

/**
 * What a voice note becomes when the waveform cannot be built (decode unsupported or failed, the
 * fetch blocked by CORS, a 404): the browser's own player, which needs neither fetch-and-decode nor
 * CORS, so the note stays playable. `preload="none"`: nothing is downloaded until it is pressed
 * (iOS Safari ignores preload and waits for the tap anyway). If even that cannot play the file
 * (a codec the browser does not know), the bubble says so with a link to the raw file instead of
 * a dead control. One error is retried once first (it may have been a network blip).
 */
export function NativeVoice({
  src,
  onDark,
  resume = false,
}: {
  src: string;
  onDark: boolean;
  /** The note was playing when the waveform failed: carry on (the browser may refuse; controls stay). */
  resume?: boolean;
}) {
  const t = useTranslations("messages");
  const [unplayable, setUnplayable] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const release = useRef<(() => void) | null>(null);
  // Decided at mount: the engine that was playing is silenced right after, which must not flip it.
  const [autoPlay] = useState(resume);
  const retried = useRef(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      release.current?.();
      if (retryTimer.current) clearTimeout(retryTimer.current);
    },
    [],
  );

  // A single error is often transient (a network blip): reload once before giving up. The link is
  // only the last resort, after that retry failed as well.
  const onError = () => {
    if (retried.current) {
      setUnplayable(true);
      return;
    }
    retried.current = true;
    retryTimer.current = setTimeout(() => {
      const el = audioRef.current;
      if (!el) return;
      el.load();
      void el.play().catch(() => {});
    }, RETRY_DELAY_MS);
  };

  if (unplayable) {
    return (
      <a
        href={src}
        target="_blank"
        rel="noopener"
        className={cn(
          // `w-60` intrinsic, `max-w-full` so it shrinks to fit a narrow mobile
          // bubble instead of overflowing its corners.
          "inline-flex w-60 max-w-full items-center gap-2 rounded-wx-sm px-3 py-2 text-xs font-medium",
          onDark
            ? "bg-wx-primary-fg/10 text-wx-primary-fg hover:bg-wx-primary-fg/20"
            : "bg-wx-bg/60 text-wx-fg hover:bg-wx-bg",
        )}
      >
        <ExternalLink size={12} className="shrink-0" />
        <span className="truncate">{t("openAudio")}</span>
      </a>
    );
  }

  return (
    // biome-ignore lint/a11y/useMediaCaption: a voice note has no caption track.
    <audio
      ref={audioRef}
      controls
      preload="none"
      src={src}
      className="h-12 w-56 max-w-full"
      autoPlay={autoPlay}
      onError={onError}
      onPlay={() => {
        release.current?.();
        release.current = claimPlayback(() => audioRef.current?.pause());
      }}
      onPause={() => {
        release.current?.();
        release.current = null;
      }}
    />
  );
}
